import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, ExternalLink, Monitor, Smartphone } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

// Fire-and-forget marketplace analytics — never blocks the UI
const track = (slug, event_type) => {
  api.post(`/turnkey-brands/${slug}/track`, { event_type }).catch(() => {});
};

// Session-length beacon — records how long visitors spend on a brand page.
// Pauses when the tab is hidden, excludes admin sessions, caps at 30 min,
// and flushes on tab exit via sendBeacon for reliability.
const useTimeOnBrand = (slug) => {
  useEffect(() => {
    if (!slug) return undefined;
    let active = 0;                // total foreground ms
    let start = Date.now();
    const isAdmin = !!localStorage.getItem("token") && (localStorage.getItem("user_role") || "").includes("admin");

    const accumulate = () => {
      if (document.visibilityState === "visible") {
        active += Date.now() - start;
      }
      start = Date.now();
    };
    const onVis = () => accumulate();
    const flush = () => {
      accumulate();
      if (active < 500) return;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/turnkey-brands/${slug}/track-time`;
      const body = JSON.stringify({ duration_ms: active, is_admin: isAdmin });
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        } else {
          fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
        }
      } catch { /* silent */ }
      active = 0;
    };

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    return () => {
      flush();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
    };
  }, [slug]);
};

export const TURNKEY_PACKAGES = [
  {
    slug: "starter-launch",
    name: "Starter Launch Package",
    price: "$3,999",
    priceNote: "Pay in Full & Save $1,000",
    highlight: false,
    tag: "Entry Point",
    includes: [
      "Exclusive Turnkey Beverage Brand",
      "Domain Name",
      "Fully Designed Website",
      "Business Email Setup",
      "Bottle Label Design",
      "2 Social Media Accounts Setup (Instagram & TikTok)",
      "1 Pallet of Your Selected Beverage (Included)",
    ],
    notIncluded: "Branding Suite not included.",
  },
  {
    slug: "complete-brand-launch",
    name: "Complete Brand Launch Package",
    price: "$4,999",
    priceNote: "Most Popular",
    highlight: true,
    tag: "Most Popular",
    includes: [
      "Everything in the Starter Launch Package",
      "Complete Branding Suite",
      "Business Card Design",
      "4x6 Flyer Design",
      "100 Printed Business Cards",
      "100 Printed 4x6 Flyers",
      "1 Branded T-Shirt",
      "1 Retractable Banner",
      "1 Pallet of Your Selected Beverage (Included)",
    ],
    notIncluded: null,
  },
  {
    slug: "launch-inventory",
    name: "Launch & Inventory Package",
    price: "Starting at $5,999",
    priceNote: "Includes Product Inventory",
    highlight: false,
    tag: "Launch + Inventory",
    includes: [
      "Exclusive Turnkey Beverage Brand",
      "Domain Name",
      "Fully Designed Website",
      "Business Email Setup",
      "Bottle Label Design",
      "2 Social Media Accounts Setup (Instagram & TikTok)",
      "Two (2) Pallets of Your Selected Beverage (Included)",
    ],
    notIncluded: "Branding Suite not included.",
  },
];

const PURCHASE_OPTIONS = [
  { value: "pay_in_full", label: "Pay in Full (Save $1,000)" },
  { value: "payment_plan", label: "Payment Plan" },
];

const TIMEFRAMES = [
  { value: "ready_now", label: "Ready Now" },
  { value: "within_30_days", label: "Within 30 Days" },
  { value: "within_60_days", label: "Within 60 Days" },
  { value: "within_90_days", label: "Within 90 Days" },
  { value: "just_exploring", label: "Just Exploring" },
];

const PREFERRED_BEVERAGES = [
  { value: "keep_existing", label: "Keep Existing Beverage Type" },
  { value: "purified_water", label: "Purified Water" },
  { value: "alkaline_water", label: "Alkaline Water" },
  { value: "spring_water", label: "Spring Water" },
  { value: "discuss_options", label: "I'd Like to Discuss My Options" },
];

const InquiryForm = ({ brand }) => {
  const [form, setForm] = useState({
    full_name: "",
    company: "",
    email: "",
    phone: "",
    package_slug: "complete-brand-launch",
    purchase_option: "pay_in_full",
    timeframe: "ready_now",
    preferred_beverage_type: "keep_existing",
    additional_info: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateValue = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.package_slug) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/turnkey-brands/inquire", { ...form, brand_slug: brand.slug });
      setSubmitted(true);
      toast.success("Inquiry received. A FLOWTALIS specialist will reach out shortly.");
    } catch {
      toast.error("Something went wrong. Please try again or email hello@flowtalis.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="ft-card" data-testid="turnkey-inquiry-success">
        <div
          className="h-14 w-14 rounded-full flex items-center justify-center mb-5"
          style={{ background: "var(--ft-gold-soft)", color: "var(--ft-gold)" }}
        >
          <Check size={26} strokeWidth={2} />
        </div>
        <div className="ft-eyebrow mb-2">Inquiry Received</div>
        <h3 className="text-2xl font-bold tracking-tight mb-3">
          Thank you. We&apos;ll be in touch.
        </h3>
        <p className="text-[var(--ft-muted)] leading-relaxed">
          A FLOWTALIS Turnkey Brand specialist will contact you within one business day to walk
          through package details, purchase terms, and next steps.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="ft-card space-y-5" data-testid="turnkey-inquiry-form">
      <div>
        <div className="ft-eyebrow mb-2">Select Your Turnkey Package</div>
        <span className="ft-gold-rule" />
        <div className="mt-5 grid gap-3">
          {TURNKEY_PACKAGES.map((pkg) => {
            const selected = form.package_slug === pkg.slug;
            return (
              <label
                key={pkg.slug}
                data-testid={`pkg-option-${pkg.slug}`}
                className={`block rounded-2xl border p-5 cursor-pointer transition-all ${
                  selected
                    ? "border-[var(--ft-gold)] bg-[var(--ft-gold-soft)]"
                    : "border-[var(--ft-border)] hover:border-[var(--ft-gold)]"
                }`}
              >
                <input
                  type="radio"
                  name="package_slug"
                  value={pkg.slug}
                  checked={selected}
                  onChange={update("package_slug")}
                  className="sr-only"
                />
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[var(--ft-black)]">{pkg.name}</span>
                      {pkg.highlight && (
                        <span
                          className="text-[0.6rem] uppercase tracking-[0.24em] font-bold px-2 py-0.5 rounded-full"
                          style={{ background: "var(--ft-gold)", color: "var(--ft-black)" }}
                        >
                          {pkg.tag}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-[var(--ft-gold)] font-bold tracking-tight text-lg">
                      {pkg.price}
                      <span className="ml-2 text-xs font-medium text-[var(--ft-muted)] uppercase tracking-[0.16em]">
                        {pkg.priceNote}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`h-5 w-5 rounded-full border-2 mt-1 shrink-0 flex items-center justify-center ${
                      selected
                        ? "border-[var(--ft-gold)] bg-[var(--ft-gold)]"
                        : "border-[var(--ft-border)]"
                    }`}
                  >
                    {selected && <Check size={12} strokeWidth={3} color="#111" />}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 pt-2">
        <div>
          <label className="ft-label" htmlFor="full_name">
            Full Name *
          </label>
          <input
            id="full_name"
            className="ft-input"
            value={form.full_name}
            onChange={update("full_name")}
            required
            data-testid="turnkey-input-name"
          />
        </div>
        <div>
          <label className="ft-label" htmlFor="company">
            Company Name <span className="text-[var(--ft-muted)] font-normal">(optional)</span>
          </label>
          <input
            id="company"
            className="ft-input"
            value={form.company}
            onChange={update("company")}
            data-testid="turnkey-input-company"
          />
        </div>
        <div>
          <label className="ft-label" htmlFor="email">
            Email Address *
          </label>
          <input
            id="email"
            type="email"
            className="ft-input"
            value={form.email}
            onChange={update("email")}
            required
            data-testid="turnkey-input-email"
          />
        </div>
        <div>
          <label className="ft-label" htmlFor="phone">
            Phone Number *
          </label>
          <input
            id="phone"
            type="tel"
            className="ft-input"
            value={form.phone}
            onChange={update("phone")}
            required
            data-testid="turnkey-input-phone"
          />
        </div>
        <div>
          <label className="ft-label" htmlFor="purchase_option">
            Purchase Option *
          </label>
          <Select value={form.purchase_option} onValueChange={updateValue("purchase_option")}>
            <SelectTrigger
              id="purchase_option"
              className="ft-input flex items-center"
              data-testid="turnkey-input-purchase"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border border-[var(--ft-border)]">
              {PURCHASE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`purchase-opt-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="ft-label" htmlFor="timeframe">
            Purchase Timeframe *
          </label>
          <Select value={form.timeframe} onValueChange={updateValue("timeframe")}>
            <SelectTrigger
              id="timeframe"
              className="ft-input flex items-center"
              data-testid="turnkey-input-timeframe"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border border-[var(--ft-border)]">
              {TIMEFRAMES.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`timeframe-opt-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="ft-label" htmlFor="preferred_beverage">
            Preferred Beverage Type *
          </label>
          <Select value={form.preferred_beverage_type} onValueChange={updateValue("preferred_beverage_type")}>
            <SelectTrigger
              id="preferred_beverage"
              className="ft-input flex items-center"
              data-testid="turnkey-input-preferred-beverage"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border border-[var(--ft-border)]">
              {PREFERRED_BEVERAGES.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`preferred-bev-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[0.7rem] text-[var(--ft-muted)] mt-1.5 leading-relaxed">
            Default:{" "}
            <span className="font-semibold text-[var(--ft-charcoal)]">
              {brand.default_beverage_type || brand.category}
            </span>
            . Your investment is in the brand — the beverage can often be customized.
          </p>
        </div>
      </div>

      <div>
        <label className="ft-label" htmlFor="additional_info">
          Additional Information
        </label>
        <textarea
          id="additional_info"
          className="ft-input min-h-[110px]"
          value={form.additional_info}
          onChange={update("additional_info")}
          placeholder="Anything else we should know? Market plans, launch region, questions…"
          data-testid="turnkey-input-info"
        />
      </div>

      <div className="pt-2 flex items-center justify-between flex-wrap gap-4">
        <span className="text-xs text-[var(--ft-muted)]">
          Selected Brand: <strong className="text-[var(--ft-black)]">{brand.brand_name}</strong>
          {brand.design_edition_label && (
            <>
              {" "}·{" "}
              <strong className="text-[var(--ft-black)]">{brand.design_edition_label}</strong>
            </>
          )}
        </span>
        <button
          type="submit"
          disabled={submitting}
          className="ft-btn-gold"
          data-testid="turnkey-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Submitting…
            </>
          ) : (
            <>
              Submit Inquiry <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const TurnkeyBrandDetail = () => {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    api
      .get(`/turnkey-brands/${slug}`)
      .then((r) => setBrand(r.data.brand))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO({
    title: brand ? `${brand.brand_name} — Turnkey Beverage Brand | FLOWTALIS™` : "Turnkey Brand — FLOWTALIS™",
    description: brand
      ? `Acquire ${brand.brand_name} — a fully built private-label beverage brand from FLOWTALIS™. ${brand.tagline}`
      : "Acquire a fully built private-label beverage brand through FLOWTALIS™ Turnkey Brands.",
    canonical: `https://flowtalis.com/turnkey/${slug}`,
  });

  useTimeOnBrand(brand ? brand.slug : null);

  const isSold = brand && (brand.status === "sold" || brand.status === "archived");

  if (loading) {
    return (
      <PublicLayout>
        <section className="py-24">
          <div className="ft-container">
            <div className="ft-card fx-skeleton h-96" data-testid="turnkey-detail-loading" />
          </div>
        </section>
      </PublicLayout>
    );
  }

  if (error || !brand) {
    return (
      <PublicLayout>
        <section className="py-24">
          <div className="ft-container max-w-xl text-center">
            <h1 className="text-3xl font-bold mb-4">Brand not found</h1>
            <p className="text-[var(--ft-muted)] mb-8">
              This turnkey brand may have already been acquired or is no longer available.
            </p>
            <Link to="/turnkey" className="ft-btn-gold" data-testid="turnkey-back-to-list">
              <ArrowLeft size={16} /> Back to Marketplace
            </Link>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="ft-section-dark py-20" data-testid="turnkey-detail-hero">
        <div className="ft-container">
          <Link
            to="/turnkey"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[var(--ft-gold)] text-sm mb-8 transition-colors"
            data-testid="turnkey-detail-back"
          >
            <ArrowLeft size={14} /> All Turnkey Brands
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-6">
                {brand.default_beverage_type || brand.category} — Turnkey Brand
              </div>
              {brand.design_edition_label && (
                <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full text-[0.65rem] uppercase tracking-[0.24em] font-bold"
                     style={{ background: "var(--ft-gold)", color: "var(--ft-black)" }}
                     data-testid="turnkey-detail-edition-badge">
                  {brand.design_edition_label}
                </div>
              )}
              <span className="ft-gold-rule-lg" />
              <h1 className="mt-6 text-white text-[clamp(2rem,4vw,3.25rem)] font-bold leading-[1.05] tracking-tight mb-6">
                {brand.brand_name}
              </h1>
              <p className="text-white/70 text-lg leading-relaxed">{brand.description}</p>

              {brand.exclusive_note && (
                <div className="mt-6 border-l-2 border-[var(--ft-gold)] pl-4 py-2 text-white/60 text-sm leading-relaxed" data-testid="turnkey-detail-exclusive-note">
                  <span className="text-[var(--ft-gold)] font-semibold uppercase tracking-[0.18em] text-[0.65rem] block mb-1">
                    Exclusive Ownership
                  </span>
                  {brand.exclusive_note}
                </div>
              )}

              <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
                {[
                  { label: "Positioning", value: brand.positioning },
                  { label: "Format", value: brand.format },
                  { label: "Domain", value: brand.domain || "Included" },
                  { label: "Status", value: brand.status_label || "Available" },
                ].map((k) => (
                  <div key={k.label}>
                    <div className="text-[0.6rem] uppercase tracking-[0.24em] text-[var(--ft-gold)] font-semibold">
                      {k.label}
                    </div>
                    <div className="text-white text-sm mt-1">{k.value || "—"}</div>
                  </div>
                ))}
              </div>

              <a
                href="#inquire"
                className="ft-btn-gold mt-10"
                data-testid="turnkey-scroll-to-inquiry"
              >
                Inquire to Purchase <ArrowRight size={16} />
              </a>
            </div>

            <div className="lg:col-span-6 relative">
              <div
                className="relative rounded-2xl overflow-hidden aspect-square flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, #0d0d0d 0%, #191919 55%, #0a0a0a 100%)",
                  boxShadow: "0 40px 90px -30px rgba(200,162,74,0.35)",
                }}
              >
                {brand.hero_image_url && (
                  <img
                    src={brand.hero_image_url}
                    alt={`${brand.brand_name} — turnkey beverage brand`}
                    className="w-full h-full object-contain p-10"
                    data-testid={`turnkey-detail-hero-img-${brand.slug}`}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-24 bg-white" data-testid="turnkey-detail-included">
        <div className="ft-container">
          <div className="max-w-2xl mb-12">
            <div className="ft-eyebrow mb-4">Brand Assets Included</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
              Everything transfers to you at handoff.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {(brand.included_assets && brand.included_assets.length > 0
              ? brand.included_assets
              : [
                  "Trademarked brand name",
                  "Registered domain name",
                  "Fully designed website",
                  "Bottle label design (production-ready)",
                  "Full brand identity system",
                  "Manufacturing coordination through FLOWTALIS",
                ]
            ).map((item, i) => (
              <div key={i} className="ft-card flex gap-3" data-testid={`turnkey-asset-${i}`}>
                <Check size={18} strokeWidth={2} style={{ color: "var(--ft-gold)" }} className="mt-1 shrink-0" />
                <span className="text-[var(--ft-charcoal)] text-sm leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY — only renders when admin has uploaded lifestyle / mockup images */}
      {brand.gallery_images && brand.gallery_images.length > 0 && (
        <section className="py-24 bg-white" data-testid="turnkey-detail-gallery">
          <div className="ft-container">
            <div className="max-w-2xl mb-12">
              <div className="ft-eyebrow mb-4">Brand Gallery</div>
              <span className="ft-gold-rule-lg" />
              <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
                See the brand <span className="ft-serif text-[var(--ft-gold)]">in context</span>.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {brand.gallery_images.map((url, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { track(brand.slug, "gallery_click"); window.open(url, "_blank"); }}
                  className="ft-card fx-hover-lift !p-0 overflow-hidden aspect-square group block"
                  data-testid={`turnkey-detail-gallery-${i}`}
                >
                  <img
                    src={url}
                    alt={`${brand.brand_name} gallery ${i + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WEBSITE PREVIEW — only renders when admin has uploaded a preview */}
      {(brand.desktop_preview_url || brand.mobile_preview_url || brand.demo_url) && (
        <section className="py-24 bg-[var(--ft-light-gray)]" data-testid="turnkey-detail-preview">
          <div className="ft-container">
            <div className="max-w-2xl mb-12">
              <div className="ft-eyebrow mb-4">Website Preview</div>
              <span className="ft-gold-rule-lg" />
              <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
                A <span className="ft-serif text-[var(--ft-gold)]">fully built</span> website — ready to transfer.
              </h2>
              <p className="mt-4 text-[var(--ft-muted)] leading-relaxed">
                Every {brand.brand_name} package includes a professionally designed brand website.
                What you see below is exactly what launches to your new domain on handoff day.
              </p>
            </div>
            <div className="grid lg:grid-cols-12 gap-6 items-start">
              {brand.desktop_preview_url && (
                <div className="lg:col-span-8">
                  <button
                    type="button"
                    onClick={() => { track(brand.slug, "preview_click"); window.open(brand.desktop_preview_url, "_blank"); }}
                    className="ft-card fx-hover-lift !p-0 overflow-hidden block w-full text-left"
                    data-testid="turnkey-detail-desktop-preview"
                  >
                    <div className="bg-[var(--ft-black)] px-4 py-2.5 flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                      <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                      <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                      <span className="ml-3 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-white/50 flex items-center gap-1.5">
                        <Monitor size={12} /> Desktop Preview
                      </span>
                    </div>
                    <img
                      src={brand.desktop_preview_url}
                      alt={`${brand.brand_name} desktop website preview`}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                </div>
              )}
              {brand.mobile_preview_url && (
                <div className="lg:col-span-4">
                  <button
                    type="button"
                    onClick={() => { track(brand.slug, "preview_click"); window.open(brand.mobile_preview_url, "_blank"); }}
                    className="ft-card fx-hover-lift !p-0 overflow-hidden block w-full text-left"
                    data-testid="turnkey-detail-mobile-preview"
                  >
                    <div className="bg-[var(--ft-black)] px-4 py-2.5 flex items-center gap-2">
                      <span className="text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-white/50 flex items-center gap-1.5">
                        <Smartphone size={12} /> Mobile Preview
                      </span>
                    </div>
                    <img
                      src={brand.mobile_preview_url}
                      alt={`${brand.brand_name} mobile website preview`}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                </div>
              )}
            </div>
            {brand.demo_url && (
              <div className="mt-8 flex justify-center">
                <a
                  href={brand.demo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track(brand.slug, "demo_click")}
                  className="ft-btn-gold"
                  data-testid="turnkey-detail-demo-btn"
                >
                  View Live Demo <ExternalLink size={16} />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* FLEXIBLE PRODUCT OPTIONS */}
      <section className="py-24 bg-white" data-testid="turnkey-flex-options">
        <div className="ft-container grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="ft-eyebrow mb-4">Flexible Product Options</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
              You&apos;re acquiring the <span className="ft-serif text-[var(--ft-gold)]">brand</span>, not the beverage.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-[var(--ft-charcoal)] leading-relaxed">
            <p>
              One of the benefits of purchasing a FLOWTALIS Turnkey Brand is flexibility. Every
              brand launches with a recommended beverage configuration (
              <strong>{brand.default_beverage_type || brand.category}</strong>) — but your
              investment is in the brand identity, and the beverage can often be customized to
              meet your business goals.
            </p>
            <p>
              For {brand.brand_name}, the following beverage options are typically available
              through our manufacturing network:
            </p>
            <ul className="grid sm:grid-cols-2 gap-2.5 pt-2">
              {(brand.available_beverage_options && brand.available_beverage_options.length > 0
                ? brand.available_beverage_options
                : [brand.default_beverage_type || brand.category]
              ).map((opt) => (
                <li key={opt} className="flex gap-2.5 text-sm">
                  <Check size={16} strokeWidth={2} style={{ color: "var(--ft-gold)" }} className="mt-0.5 shrink-0" />
                  <span>
                    {opt}
                    {opt === (brand.default_beverage_type || brand.category) && (
                      <span className="ml-2 text-[0.65rem] uppercase tracking-[0.2em] text-[var(--ft-gold)] font-semibold">
                        Recommended
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {brand.manufacturing_notes && (
              <p className="text-[var(--ft-muted)] text-sm pt-2 leading-relaxed">
                {brand.manufacturing_notes}
              </p>
            )}
            <p className="pt-2">
              Please indicate your preferred beverage type in the inquiry form below — or select
              &quot;I&apos;d Like to Discuss My Options&quot; if you&apos;d prefer to explore alternatives with our
              specialist.
            </p>
          </div>
        </div>
      </section>

      {/* PACKAGES OVERVIEW */}
      <section className="py-24 bg-[var(--ft-light-gray)]" data-testid="turnkey-detail-packages">
        <div className="ft-container">
          <div className="max-w-2xl mb-12">
            <div className="ft-eyebrow mb-4">Turnkey Packages</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
              Choose the launch tier that fits your ambition.
            </h2>
            <p className="mt-4 text-[var(--ft-muted)] leading-relaxed">
              All packages transfer full ownership of the {brand.brand_name} brand. Select your
              preferred package in the inquiry form below — our team will handle the rest.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TURNKEY_PACKAGES.map((pkg) => (
              <div
                key={pkg.slug}
                className={`ft-card flex flex-col ${
                  pkg.highlight ? "ring-1 ring-[var(--ft-gold)]" : ""
                }`}
                data-testid={`turnkey-pkg-card-${pkg.slug}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[0.65rem] uppercase tracking-[0.24em] font-semibold text-[var(--ft-gold)]">
                    {pkg.tag}
                  </div>
                  {pkg.highlight && (
                    <span
                      className="text-[0.6rem] uppercase tracking-[0.24em] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: "var(--ft-gold)", color: "var(--ft-black)" }}
                    >
                      Most Popular
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold tracking-tight text-[var(--ft-black)]">
                  {pkg.name}
                </h3>
                <div className="mt-3 text-3xl font-bold text-[var(--ft-black)] tracking-tight">
                  {pkg.price}
                </div>
                <div className="text-xs text-[var(--ft-muted)] uppercase tracking-[0.18em] mt-1 font-semibold">
                  {pkg.priceNote}
                </div>
                <div className="ft-gold-rule my-5" />
                <ul className="space-y-2.5 flex-1">
                  {pkg.includes.map((item, i) => (
                    <li key={i} className="flex gap-2.5 text-sm text-[var(--ft-charcoal)] leading-relaxed">
                      <Check size={16} strokeWidth={2} style={{ color: "var(--ft-gold)" }} className="mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {pkg.notIncluded && (
                  <div className="mt-5 pt-4 border-t border-[var(--ft-border)] text-xs text-[var(--ft-muted)]">
                    {pkg.notIncluded}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="inquire" className="py-24 bg-white" data-testid="turnkey-detail-inquire">
        <div className="ft-container max-w-3xl">
          <div className="mb-10">
            <div className="ft-eyebrow mb-4">Inquire to Purchase</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
              Acquire <span className="ft-serif text-[var(--ft-gold)]">{brand.brand_name}</span>.
            </h2>
            <p className="mt-4 text-[var(--ft-muted)] leading-relaxed">
              Complete the short form below and a FLOWTALIS Turnkey Brand specialist will contact
              you within one business day. No payment is taken here — this initiates a consultative
              purchase conversation.
            </p>
          </div>
          {isSold ? (
            <div className="ft-card text-center py-12" data-testid="turnkey-detail-sold-notice">
              <div className="text-[0.65rem] uppercase tracking-[0.28em] font-bold text-[var(--ft-gold)] mb-3">
                No Longer Available
              </div>
              <h3 className="text-2xl font-bold tracking-tight mb-3">
                {brand.brand_name} has been acquired.
              </h3>
              <p className="text-[var(--ft-muted)] max-w-md mx-auto leading-relaxed">
                This turnkey brand is no longer available. Browse other available brands in the
                marketplace or book a consultation to build a fully custom private-label brand.
              </p>
              <Link to="/turnkey" className="ft-btn-primary mt-6 inline-flex" data-testid="turnkey-sold-browse">
                <ArrowLeft size={16} /> Browse Available Brands
              </Link>
            </div>
          ) : (
            <InquiryForm brand={brand} />
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default TurnkeyBrandDetail;
